import React, { useDeferredValue, useEffect, useMemo, useState, startTransition } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField
} from '@mui/material';
import './LibelleManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const normalizeLibelle = (libelle) => {
  const createdValue = libelle.created_at || libelle.createdAt || null;
  let createdLabel = '-';

  if (createdValue) {
    const date = new Date(createdValue);
    if (!Number.isNaN(date.getTime())) {
      createdLabel = date.toLocaleDateString('fr-FR');
    }
  }

  return {
    ...libelle,
    createdLabel
  };
};

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(
      text.includes('403 Forbidden')
        ? 'Le serveur de production bloque la methode DELETE avant Symfony.'
        : `Reponse serveur inattendue (${response.status})`
    );
  }

  return response.json();
};

function LibelleManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [texteInput, setTexteInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [libelles, setLibelles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [clearExisting, setClearExisting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLibelle, setSelectedLibelle] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const deferredLibelles = useDeferredValue(libelles);

  useEffect(() => {
    fetchLibelles();
  }, []);

  const fetchLibelles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/libelles`, { headers: getAuthHeaders() });
      const data = await parseJsonResponse(response);

      if (data.success) {
        const normalized = Array.isArray(data.data) ? data.data.map(normalizeLibelle) : [];
        startTransition(() => {
          setLibelles(normalized);
          setPage(0);
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des libelles:', error);
    }
  };

  const visibleLibelles = useMemo(() => {
    const start = page * rowsPerPage;
    return deferredLibelles.slice(start, start + rowsPerPage);
  }, [deferredLibelles, page, rowsPerPage]);

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleImportTexte = async (e) => {
    e.preventDefault();
    if (!texteInput.trim()) {
      showMessage('Veuillez entrer au moins un libelle', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/libelles/import`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          libelles_text: texteInput,
          clear_existing: clearExisting
        })
      });

      const data = await parseJsonResponse(response);
      if (data.success) {
        showMessage(
          `Import reussi: ${data.created_count} crees, ${data.updated_count} mis a jour`,
          'success'
        );
        setTexteInput('');
        await fetchLibelles();
      } else {
        showMessage(`Erreur: ${data.error}`, 'error');
      }
    } catch (error) {
      showMessage(`Erreur: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleImportFichier = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showMessage('Veuillez selectionner un fichier .xlsx', 'error');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('libelles_file', selectedFile);
      formData.append('clear_existing', clearExisting ? 'true' : 'false');

      const response = await fetch(`${API_BASE_URL}/libelles/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const data = await parseJsonResponse(response);
      if (data.success) {
        showMessage(
          `Import reussi: ${data.created_count} crees, ${data.updated_count} mis a jour`,
          'success'
        );
        setSelectedFile(null);
        await fetchLibelles();
      } else {
        showMessage(`Erreur: ${data.error}`, 'error');
      }
    } catch (error) {
      showMessage(`Erreur: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = (libelle) => {
    setSelectedLibelle(libelle);
    setEditName(libelle.name);
    setEditDescription(libelle.description || '');
    setEditEnabled(libelle.enabled);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/libelles/${selectedLibelle.id}`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: editName,
          description: editDescription,
          enabled: editEnabled
        })
      });

      const data = await parseJsonResponse(response);
      if (data.success) {
        showMessage('Libelle mis a jour', 'success');
        if (data.data) {
          const normalized = normalizeLibelle(data.data);
          startTransition(() => {
            setLibelles((current) =>
              current.map((item) => (item.id === normalized.id ? { ...item, ...normalized } : item))
            );
          });
        }
        setEditDialogOpen(false);
      } else {
        showMessage(`Erreur: ${data.error}`, 'error');
      }
    } catch (error) {
      showMessage(`Erreur: ${error.message}`, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression du libelle ?')) {
      return;
    }

    try {
      let response = await fetch(`${API_BASE_URL}/libelles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.status === 403) {
        response = await fetch(`${API_BASE_URL}/libelles/${id}/delete`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({})
        });
      }

      const data = await parseJsonResponse(response);
      if (data.success) {
        showMessage('Libelle supprime', 'success');
        startTransition(() => {
          setLibelles((current) => current.filter((item) => item.id !== id));
          setPage((currentPage) => {
            const nextCount = Math.max(deferredLibelles.length - 1, 0);
            const maxPage = Math.max(Math.ceil(nextCount / rowsPerPage) - 1, 0);
            return Math.min(currentPage, maxPage);
          });
        });
      } else {
        showMessage(`Erreur: ${data.error}`, 'error');
      }
    } catch (error) {
      showMessage(`Erreur: ${error.message}`, 'error');
    }
  };

  const handlePageChange = (_event, nextPage) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="lg" className="libelle-management">
      <Paper className="libelle-paper" elevation={3}>
        <h2>Gestion des Libelles</h2>

        {message && (
          <Alert severity={messageType} style={{ marginBottom: '20px' }}>
            {message}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_e, newValue) => setTabValue(newValue)}>
            <Tab label="Import par texte" />
            <Tab label="Import par fichier .xlsx" />
            <Tab label="Libelles" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box className="tab-content">
            <h3>Entrez vos libelles</h3>
            <p className="help-text">
              Separez les libelles par des virgules ou des retours a la ligne
            </p>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder={'moteur, turbo, alternateur\nbatterie\nfiltre\n...'}
              value={texteInput}
              onChange={(e) => setTexteInput(e.target.value)}
              disabled={loading}
              variant="outlined"
              className="libelle-textarea"
            />

            <Box className="import-options">
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={clearExisting}
                    onChange={(e) => setClearExisting(e.target.checked)}
                    disabled={loading}
                  />
                )}
                label="Vider les libelles existants avant l'import"
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleImportTexte}
              disabled={loading || !texteInput.trim()}
              className="import-button"
            >
              {loading ? <CircularProgress size={24} /> : 'Importer les libelles'}
            </Button>
          </Box>
        )}

        {tabValue === 1 && (
          <Box className="tab-content">
            <h3>Importez un fichier .xlsx</h3>
            <p className="help-text">
              Format attendu: Colonne A = Nom, Colonne B (optionnel) = Description
            </p>

            <Box className="file-upload">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                disabled={loading}
                id="file-input"
              />
              <label htmlFor="file-input" className="file-label">
                {selectedFile ? selectedFile.name : 'Cliquez pour selectionner un fichier'}
              </label>
            </Box>

            <Box className="import-options">
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={clearExisting}
                    onChange={(e) => setClearExisting(e.target.checked)}
                    disabled={loading}
                  />
                )}
                label="Vider les libelles existants avant l'import"
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleImportFichier}
              disabled={loading || !selectedFile}
              className="import-button"
            >
              {loading ? <CircularProgress size={24} /> : 'Importer le fichier'}
            </Button>
          </Box>
        )}

        {tabValue === 2 && (
          <Box className="tab-content">
            <h3>Libelles ({libelles.length})</h3>
            {libelles.length === 0 ? (
              <Alert severity="info">Aucun libelle pour le moment. Importez-en via les onglets precedents.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Libelle</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="center"><strong>Statut</strong></TableCell>
                      <TableCell align="center"><strong>Cree</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleLibelles.map((libelle) => (
                      <TableRow key={libelle.id}>
                        <TableCell style={{ fontWeight: 'bold' }}>{libelle.name}</TableCell>
                        <TableCell>{libelle.description || '-'}</TableCell>
                        <TableCell align="center">
                          {libelle.enabled ? (
                            <span style={{ color: 'green', fontWeight: 'bold' }} title="Actif">Oui</span>
                          ) : (
                            <span style={{ color: 'red' }}>Non</span>
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ fontSize: '0.85em' }}>
                          {libelle.createdLabel}
                        </TableCell>
                        <TableCell align="center" className="action-cell">
                          <IconButton
                            size="small"
                            onClick={() => handleEditOpen(libelle)}
                            title="Modifier"
                            className="action-button edit-button"
                          >
                            Edit
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(libelle.id)}
                            title="Supprimer"
                            color="error"
                            className="action-button delete-button"
                          >
                            Del
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={libelles.length}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[25, 50, 100, 250]}
                  labelRowsPerPage="Lignes par page"
                />
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le libelle</DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
          <TextField
            fullWidth
            label="Nom"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={editEnabled}
                onChange={(e) => setEditEnabled(e.target.checked)}
              />
            )}
            label="Actif"
            style={{ marginTop: '15px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default LibelleManagement;
